/**
 * Sponsor payment — third step of the sponsorship sub-flow.
 *
 * Mirrors the team-registration PaymentPage: creates a Stripe
 * PaymentIntent (checkout), renders Stripe Elements, and on Stripe
 * success calls our /confirm to claim the slot and create the sponsor
 * assignment. The confirmation result is stashed in FlowContext for the
 * done page.
 */
import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe, type Stripe } from '@stripe/stripe-js';

import type { EmbedClient, SponsorshipCheckoutResponse } from '@/api/embed';
import { useFlow } from '@/state/FlowContext';
import { useFlowNavigate } from '@/state/useFlowNavigate';
import { PaymentForm } from '@/components/PaymentForm';
import { themedStripeAppearance } from '@/styles/stripeAppearance';
import './PaymentPage.css';

const STRIPE_PUBLISHABLE_KEY = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY ?? '';

interface Props {
  client: EmbedClient;
}

function money(cents: number): string {
  return `$${(cents / 100).toLocaleString('en-US', {
    minimumFractionDigits: cents % 100 ? 2 : 0,
    maximumFractionDigits: 2,
  })}`;
}

export function SponsorPaymentPage({ client }: Props) {
  const { eventId, sponsorshipId } = useParams<{ eventId: string; sponsorshipId: string }>();
  const navigate = useFlowNavigate();
  const { selectedSponsorship, sponsorForm, setSponsorResult } = useFlow();

  const [checkout, setCheckout] = useState<SponsorshipCheckoutResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const stripe = useMemo<Promise<Stripe | null> | null>(() => {
    if (!STRIPE_PUBLISHABLE_KEY) return null;
    return loadStripe(STRIPE_PUBLISHABLE_KEY);
  }, []);

  useEffect(() => {
    if (!eventId || !sponsorshipId) return;
    if (!selectedSponsorship || !sponsorForm) {
      navigate(`/events/${eventId}/sponsor`, { replace: true });
      return;
    }
    client
      .checkoutSponsorship(eventId, sponsorshipId, sponsorForm)
      .then(setCheckout)
      .catch((err) => {
        setError(
          err?.response?.status === 409
            ? 'This sponsorship just sold out. Please pick another level.'
            : 'Could not start checkout. Please try again.',
        );
      });
  }, [client, eventId, sponsorshipId, selectedSponsorship, sponsorForm, navigate]);

  const runConfirm = async () => {
    if (!eventId || !sponsorshipId || !sponsorForm || !checkout) return;
    const result = await client.confirmSponsorship(eventId, sponsorshipId, {
      ...sponsorForm,
      stripe_payment_intent_id: checkout.stripe_payment_intent_id,
    });
    setSponsorResult(result);
    navigate(`/events/${eventId}/sponsor/done`);
  };

  if (!selectedSponsorship || !sponsorForm) return null;

  if (error) {
    return <div role="alert" className="tn-payment__error">{error}</div>;
  }

  if (!checkout) {
    return <div className="tn-payment__loading">Preparing checkout…</div>;
  }

  const total = checkout.order_summary.total_cents;

  if (!STRIPE_PUBLISHABLE_KEY) {
    // Dev mode without Stripe configured — skip straight to confirm so
    // the rest of the flow is testable (matches the team PaymentPage).
    return (
      <div className="tn-card tn-payment__dev-skip">
        <h2>Payment (dev mode)</h2>
        <p>Stripe publishable key not set. Skipping payment for testing.</p>
        <button
          className="tn-btn tn-btn--primary"
          onClick={() => { void runConfirm(); }}
        >
          Mark as paid (dev)
        </button>
      </div>
    );
  }

  if (!stripe) {
    return <div className="tn-payment__loading">Preparing checkout…</div>;
  }

  return (
    <div className="tn-payment">
      <h1 className="tn-payment__title">Payment</h1>
      <p className="tn-payment__subtitle">
        {selectedSponsorship.tier_name}
        {checkout.order_summary.requires_approval ? ' · charged once approved' : ''}
      </p>

      <div className="tn-payment__breakdown">
        <div className="tn-payment__line">
          <span>Sponsorship</span>
          <span>{money(checkout.order_summary.price_cents)}</span>
        </div>
        {(checkout.order_summary.offer_addon_cents ?? 0) > 0 ? (
          <div className="tn-payment__line">
            <span>Digital Contact Card</span>
            <span>{money(checkout.order_summary.offer_addon_cents ?? 0)}</span>
          </div>
        ) : null}
        {(checkout.order_summary.platform_fee_cents ?? 0) > 0 ? (
          <div className="tn-payment__line">
            <span>Platform fee</span>
            <span>{money(checkout.order_summary.platform_fee_cents ?? 0)}</span>
          </div>
        ) : null}
        {checkout.order_summary.processing_fee_cents > 0 ? (
          <div className="tn-payment__line">
            <span>Processing fee</span>
            <span>{money(checkout.order_summary.processing_fee_cents)}</span>
          </div>
        ) : null}
        <div className="tn-payment__line tn-payment__line--total">
          <span>Total</span>
          <span>{money(total)}</span>
        </div>
      </div>

      <Elements
        stripe={stripe}
        options={{
          clientSecret: checkout.stripe_client_secret,
          appearance: themedStripeAppearance(),
        }}
      >
        <PaymentForm submitLabel="Pay and sponsor" onSuccess={runConfirm} />
      </Elements>
    </div>
  );
}
